import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { isSymbolicaUser } from '@/lib/utils';
import { useAppSession } from '@/contexts/SessionContext';
import { useAuth } from '@/hooks/useAuth';
import { useAgentStatus } from '@/hooks/useAgentStatus';
import { useSettings } from '@/hooks/useSettings';
import { useChatInput } from '@/components/ChatInput/useChatInput';
import { useConversationHydration } from '@/hooks/useConversationHydration';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useSessionTitlesStore } from '@/stores/sessionTitles';
import { Titles } from '@/lib/navigation';
import type { TimelineItem } from '@/hooks/useChatStore';
import type { SubagentTimelineItem } from '@/hooks/useChat';
import type { ActivityVisibility } from '@/hooks/useSettings';
import {
  DisconnectBanner,
  PowerOnBanner,
  AgentErrorBanner,
  AgentTransitionBanner,
  AgentPoweredOffBanner,
} from '@/components/ChatPage/StatusBanners';
import { ChatErrorBanner } from '@/components/ChatPage/ChatErrorBanner';
import { DemoSignInPrompt } from '@/components/DemoSignInPrompt';
import { useDevBannerOverrides } from '@/lib/devBannerTriggers';
import { PaperChatTopBar } from './PaperChatTopBar';
import { PaperMobileArtifactCard } from './PaperMobileArtifactCard';
import { PaperChatInput } from './PaperChatInput';
import { ChatColumn } from './ChatColumn';
import { PaperMessageCard } from './PaperMessageCard';
import { PaperWorkingBubble } from './PaperWorkingBubble';
import { PaperConversationShell } from './PaperConversationShell';

const shouldShowItem = (item: TimelineItem, visibility: ActivityVisibility): boolean => {
  if (visibility === 'show_all') return true;
  return (
    item.kind === 'user' ||
    item.kind === 'agent' ||
    item.kind === 'system' ||
    item.kind === 'download' ||
    item.kind === 'auth_request'
  );
};

export const PaperConversationPage = () => {
  const { id } = useParams<{ id: string }>();
  const { session, actions, artifacts, agentError, dismissAgentError } = useAppSession();
  const { chat, chatHistory, isPoweringOn } = session;
  const loading = useConversationHydration(id);

  const auth = useAuth();
  const { settings } = useSettings();
  const getCachedTitle = useSessionTitlesStore((s) => s.get);

  const sessionRow = useMemo(
    () => chatHistory.sessions.find((s) => s.id === id),
    [chatHistory.sessions, id],
  );
  const title = sessionRow?.title ?? (id ? getCachedTitle(id) ?? 'Chat' : 'Chat');

  useDocumentTitle(Titles.agent(title));

  const { hasActiveRun, activeAgentStatus } = useAgentStatus(
    id ?? null,
    chat.activeChat?.isRunning,
    chatHistory.sessions,
  );

  const activeChat = chat.activeChat;
  const isRunning = (activeChat?.isRunning ?? false) || hasActiveRun;
  const runningSubagents = useMemo(
    () =>
      activeChat?.timeline.filter(
        (item): item is SubagentTimelineItem =>
          item.kind === 'subagent' && item.status === 'running',
      ) ?? [],
    [activeChat?.timeline],
  );

  const isExampleSession = sessionRow?.is_example ?? false;
  const requiresSignIn = !auth.isAuthenticated && isExampleSession;
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);

  const devOverrides = useDevBannerOverrides();
  const isPaymentRequired =
    activeChat?.error?.type === 'payment-required'
    || devOverrides.paymentRequired
    || devOverrides.paymentRequiredAnon;
  const rawChatInput = useChatInput({
    isRunning,
    agentStatus: activeAgentStatus,
    sendInFlight: chat.sendInFlight,
    blocked: isPaymentRequired,
  });
  const demoOnSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setShowSignInPrompt(true);
  }, []);
  const chatInput = useMemo(
    () => (requiresSignIn ? { ...rawChatInput, onSubmit: demoOnSubmit } : rawChatInput),
    [requiresSignIn, rawChatInput, demoOnSubmit],
  );

  const canViewAllActivity = isSymbolicaUser(auth.user?.email);
  const effectiveVisibility: ActivityVisibility = canViewAllActivity
    ? settings.activityVisibility
    : 'results_only';

  const visibleTimeline = useMemo(
    () =>
      activeChat?.timeline.filter((item) => shouldShowItem(item, effectiveVisibility)) ?? [],
    [activeChat?.timeline, effectiveVisibility],
  );

  const showWorkingBubble =
    (activeChat?.isRunning ?? false) &&
    activeAgentStatus !== 'starting' &&
    activeAgentStatus !== 'stopping' &&
    activeAgentStatus !== 'off' &&
    (!(activeChat?.isStreamingAgentText ?? false) || runningSubagents.length > 0) &&
    !activeChat?.timeline.some(
      (item) => item.kind === 'auth_request' && item.status === 'pending',
    );

  const scrollRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const keyboardScrollPinUntilRef = useRef(0);
  const prevTimelineLenRef = useRef(0);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    isNearBottomRef.current = true;
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    isNearBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  useEffect(() => {
    const newUserMessage =
      visibleTimeline.length > prevTimelineLenRef.current &&
      visibleTimeline[prevTimelineLenRef.current]?.kind === 'user';
    prevTimelineLenRef.current = visibleTimeline.length;
    if (newUserMessage || isNearBottomRef.current) {
      scrollToBottom();
    }
  }, [visibleTimeline, showWorkingBubble, scrollToBottom]);

  useEffect(() => {
    const visualViewport = window.visualViewport;
    let firstFrame: number | null = null;
    let secondFrame: number | null = null;
    const timers: number[] = [];

    const clearPending = () => {
      if (firstFrame !== null) {
        window.cancelAnimationFrame(firstFrame);
        firstFrame = null;
      }
      if (secondFrame !== null) {
        window.cancelAnimationFrame(secondFrame);
        secondFrame = null;
      }
      for (const timer of timers) {
        window.clearTimeout(timer);
      }
      timers.length = 0;
    };

    const isTextareaFocused = () => document.activeElement instanceof HTMLTextAreaElement;
    const forcePinnedScroll = () =>
      keyboardScrollPinUntilRef.current > window.performance.now();

    const scheduleScrollToBottom = (force = false) => {
      if (!force && !isNearBottomRef.current) return;
      clearPending();

      firstFrame = window.requestAnimationFrame(() => {
        firstFrame = null;
        scrollToBottom();
        secondFrame = window.requestAnimationFrame(() => {
          secondFrame = null;
          scrollToBottom();
        });
      });
      timers.push(window.setTimeout(scrollToBottom, 120));
      timers.push(window.setTimeout(scrollToBottom, 320));
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (!(event.target instanceof HTMLTextAreaElement)) return;
      if (!isNearBottomRef.current) return;
      keyboardScrollPinUntilRef.current = window.performance.now() + 1000;
      scheduleScrollToBottom(true);
    };

    const handleFocusOut = (event: FocusEvent) => {
      if (!(event.target instanceof HTMLTextAreaElement)) return;
      keyboardScrollPinUntilRef.current = 0;
      clearPending();
    };

    const handleViewportChange = () => {
      if (!isTextareaFocused()) return;
      scheduleScrollToBottom(forcePinnedScroll());
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    window.addEventListener('resize', handleViewportChange);
    visualViewport?.addEventListener('resize', handleViewportChange);
    visualViewport?.addEventListener('scroll', handleViewportChange);

    return () => {
      clearPending();
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      window.removeEventListener('resize', handleViewportChange);
      visualViewport?.removeEventListener('resize', handleViewportChange);
      visualViewport?.removeEventListener('scroll', handleViewportChange);
    };
  }, [scrollToBottom]);

  const isLoading = loading || !activeChat;

  return (
    <>
      <PaperConversationShell
        loading={isLoading}
        scrollRef={scrollRef}
        onScroll={handleScroll}
        topBar={<PaperChatTopBar title={title} />}
        dockedCards={
          artifacts.hasArtifact && !artifacts.artifactOpen && (
            <PaperMobileArtifactCard
              className="desktop:hidden"
              title={title}
              onOpen={() => artifacts.setArtifactOpen(true)}
            />
          )
        }
        messages={
          activeChat && (
            <ChatColumn className="flex flex-col gap-[22px]">
              {visibleTimeline.map((item) => (
                <PaperMessageCard
                  key={item.id}
                  item={item}
                  sessionId={activeChat.sessionId}
                  onAuthComplete={chat.markAuthComplete}
                />
              ))}
              {devOverrides.selfInitiatedTask && (
                <PaperMessageCard
                  item={{ kind: 'system', id: 'dev:selfInitiated', text: 'Agent is working on a self-initiated task…' }}
                  sessionId={activeChat.sessionId}
                  onAuthComplete={chat.markAuthComplete}
                />
              )}
              {(showWorkingBubble || devOverrides.workingBubble) && (
                <div>
                  <PaperWorkingBubble
                    show={true}
                    status={activeChat.workingStatus ?? ''}
                    subagents={runningSubagents}
                  />
                </div>
              )}
            </ChatColumn>
          )
        }
        banners={
          activeChat && (
            <>
              <ChatErrorBanner
                error={
                  devOverrides.paymentRequired || devOverrides.paymentRequiredAnon
                    ? { type: 'payment-required' }
                    : devOverrides.chatErrorUnknown
                      ? { type: 'unknown', message: '' }
                      : activeChat.error
                }
                forceAnon={devOverrides.paymentRequiredAnon}
              />
              <DisconnectBanner show={chatHistory.disconnected || devOverrides.disconnect} />
              <PowerOnBanner show={isPoweringOn || devOverrides.powerOn} />
              <AgentErrorBanner
                show={!!agentError || devOverrides.agentError}
                error={agentError ?? (devOverrides.agentError ? 'Sample agent error — triggered from dev panel.' : null)}
                onDismiss={dismissAgentError}
              />
              <AgentTransitionBanner
                show={
                  activeAgentStatus === 'starting' ||
                  activeAgentStatus === 'stopping' ||
                  devOverrides.agentStarting ||
                  devOverrides.agentStopping
                }
                agentStatus={
                  devOverrides.agentStarting
                    ? 'starting'
                    : devOverrides.agentStopping
                      ? 'stopping'
                      : activeAgentStatus
                }
              />
              <AgentPoweredOffBanner
                show={activeAgentStatus === 'off' || devOverrides.poweredOff}
                onStartAgent={id ? () => actions.handleStartAgent(id) : undefined}
              />
            </>
          )
        }
        chatInput={<PaperChatInput {...chatInput} isRunning={isRunning} />}
      />
      {(showSignInPrompt || devOverrides.demoSignIn) && (
        <DemoSignInPrompt
          onSignIn={() => {
            setShowSignInPrompt(false);
            auth.signIn();
          }}
          onCancel={() => setShowSignInPrompt(false)}
        />
      )}
    </>
  );
};
